import { Locale, RedisTtlConfig, Scope } from '@headless-workspace/config';
import {
    PreviewOptions,
    CachedRepository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
} from '@headless-workspace/data';
import { CachingHelpers, DecoderHelpers } from '@headless-workspace/utils';
import { HomeSlotDTO, HomeSlotsContainerDTO, HomeSlotsValue, mapHomeSlots } from '../../../../src';

type HomeSlotsParams = {
    locale: Locale;
};
const CACHE_SERVICE_HOME_SLOTS_PREFIX = 'slots';
const CACHE_SERVICE_HOME_SLOTS_KEY = 'homepage';

export interface HomeSlotsRepositorySpec {
    getSlots(params: HomeSlotsParams): Promise<Result<HomeSlotsValue>>;
}

export class HomeSlotsRepository extends CachedRepository implements HomeSlotsRepositorySpec {
    path = '/content/v1';

    constructor() {
        super(memoryCacheAuthStrategy, memoryCacheRefreshTokenStrategy, memoryCacheQueryParamStrategy);
    }

    async getRemoteHomeSlots({
        locale,
        isPreviewable = false,
    }: {
        locale: Locale;
        isPreviewable?: boolean;
    }): Promise<Result<HomeSlotsValue>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/slots`, {
                scope: Scope.HomePage,
                locale,
                isPreviewable,
            });
            const array = HomeSlotsContainerDTO.parse(response);
            const slots = DecoderHelpers.safeParseArray(array.slots, HomeSlotDTO);

            if (slots.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(mapHomeSlots({ slots }, locale));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async getSlots(params: { locale: Locale }): Promise<Result<HomeSlotsValue>> {
        const { locale } = params;

        if (this.isPreviewModeEnabled()) {
            return this.getRemoteHomeSlots({ locale, isPreviewable: true });
        }

        return this.withCache(
            locale,
            CACHE_SERVICE_HOME_SLOTS_PREFIX,
            CachingHelpers.createCacheKey(CACHE_SERVICE_HOME_SLOTS_KEY, params),
            {
                ...RedisTtlConfig,
                tags: CachingHelpers.createTags(CACHE_SERVICE_HOME_SLOTS_PREFIX, CACHE_SERVICE_HOME_SLOTS_KEY, params),
            },
            () => this.getRemoteHomeSlots(params),
        );
    }
}
